import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';

const INTERACTION_TYPES = [
  { value: 'note', label: '📝 Note', color: '#6366f1' },
  { value: 'call', label: '📞 Call', color: '#059669' },
  { value: 'visit', label: '🏠 Visit', color: '#d97706' },
  { value: 'sms', label: '💬 SMS', color: '#2563eb' },
  { value: 'whatsapp', label: '📱 WhatsApp', color: '#16a34a' },
  { value: 'email', label: '✉️ Email', color: '#7c3aed' },
  { value: 'other', label: '📌 Other', color: '#6b7280' },
];

export default function ClientApproval() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeFilter, setActiveFilter] = useState('all');

  const [reviewModal, setReviewModal] = useState(false);
  const [actionModal, setActionModal] = useState(false);
  const [returnModal, setReturnModal] = useState(false); // NEW: Return-to-officer modal
  const [selectedClient, setSelectedClient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [products, setProducts] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');

  const [actionType, setActionType] = useState('approved');
  const [actionNotes, setActionNotes] = useState('');
  const [recommendedProduct, setRecommendedProduct] = useState('');
  const [recommendedLimit, setRecommendedLimit] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // ═══════════════════════════════════════════════════════════
  // NEW: Return-to-officer state
  // ═══════════════════════════════════════════════════════════
  const [returnReason, setReturnReason] = useState('');
  const [returnAreas, setReturnAreas] = useState({
    personal: false,
    home: false,
    business: false,
    collateral: false,
    documents: false,
  });
  const [returnLoading, setReturnLoading] = useState(false);

  const [interactions, setInteractions] = useState([]);
  const [interactionsLoading, setInteractionsLoading] = useState(false);
  const [interactionModalOpen, setInteractionModalOpen] = useState(false);
  const [newInteractionType, setNewInteractionType] = useState('note');
  const [newInteractionContent, setNewInteractionContent] = useState('');
  const [savingInteraction, setSavingInteraction] = useState(false);

  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [verifyingDocId, setVerifyingDocId] = useState(null);
  const [rejectingDocId, setRejectingDocId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const [lightbox, setLightbox] = useState(null);
  const [lightboxZoomed, setLightboxZoomed] = useState(false);

  const [recoveringRefs, setRecoveringRefs] = useState(false);
  const recoveredClientIdRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (lightbox) setLightbox(null);
        if (lightboxZoomed) setLightboxZoomed(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightbox, lightboxZoomed]);

  useEffect(() => {
    if (lightbox) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightbox]);

  useEffect(() => {
    const fetchAllPending = async () => {
      setLoading(true);
      try {
        const [approvalRes, managerRes] = await Promise.allSettled([
          api.get('/clients?status=pending-approval&limit=200'),
          api.get('/clients?status=pending-manager-review&limit=200'),
        ]);

        const approvalClients = approvalRes.status === 'fulfilled'
          ? (approvalRes.value.data?.data?.clients || approvalRes.value.data?.clients || [])
          : [];
        const managerClients = managerRes.status === 'fulfilled'
          ? (managerRes.value.data?.data?.clients || managerRes.value.data?.clients || [])
          : [];

        const mergedMap = new Map();
        [...approvalClients, ...managerClients].forEach(c => {
          mergedMap.set(c._id, c);
        });

        setClients(Array.from(mergedMap.values()));
      } catch (err) {
        console.error('Failed to fetch pending clients:', err);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllPending();

    api.get('/loan-products?isActive=true').then(res => {
      setProducts(res.data.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (actionType === 'approved' && recommendedProduct) {
      const prod = products.find(p => p._id === recommendedProduct);
      setRecommendedLimit(prod ? prod.maxAmount : '');
    } else {
      setRecommendedLimit('');
    }
  }, [recommendedProduct, actionType, products]);

  const getFilteredClients = () => {
    let filtered = clients;

    if (activeFilter !== 'all') {
      filtered = filtered.filter(c => c.status === activeFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(c =>
        (c.firstName?.toLowerCase() || '').includes(term) ||
        (c.lastName?.toLowerCase() || '').includes(term) ||
        (c.clientNumber?.toLowerCase() || '').includes(term) ||
        (c.phone?.toLowerCase() || '').includes(term) ||
        (`${c.firstName || ''} ${c.lastName || ''}`.toLowerCase()).includes(term)
      );
    }

    switch (sortBy) {
      case 'oldest':
        filtered = [...filtered].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'name':
        filtered = [...filtered].sort((a, b) => {
          const nameA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          const nameB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      case 'newest':
      default:
        filtered = [...filtered].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
    }

    return filtered;
  };

  const filteredClients = getFilteredClients();

  const loadInteractions = async (clientId) => {
    setInteractionsLoading(true);
    try {
      const res = await api.get(`/clients/interactions?clientId=${clientId}`);
      setInteractions(res.data.data || []);
    } catch {
      setInteractions([]);
    }
    setInteractionsLoading(false);
  };

  const handleSaveInteraction = async () => {
    if (!newInteractionContent.trim() || !selectedClient) return;
    setSavingInteraction(true);
    try {
      const payload = {
        client: selectedClient._id,
        step: 0,
        stepTitle: 'Manager Review',
        type: newInteractionType,
        content: newInteractionContent.trim(),
      };
      const res = await api.post('/clients/interactions', payload);
      setInteractions(prev => [res.data.data, ...prev]);
      setNewInteractionType('note');
      setNewInteractionContent('');
      setInteractionModalOpen(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save interaction');
    }
    setSavingInteraction(false);
  };

  const recoverReferences = async (clientId) => {
    if (recoveringRefs) return;
    setRecoveringRefs(true);
    try {
      const res = await api.post(`/clients/${clientId}/recover-references`);
      if (res.data?.data) {
        setSelectedClient(res.data.data);
      }
    } catch (err) {
      console.error('Failed to recover references:', err);
    } finally {
      setRecoveringRefs(false);
    }
  };

  const openReviewModal = async (clientId) => {
    setReviewLoading(true);
    setReviewModal(true);
    setActiveSection('personal');
    setPreviewDoc(null);
    setLightbox(null);
    setRecoveringRefs(false);
    recoveredClientIdRef.current = null;
    try {
      const res = await api.get(`/clients/${clientId}`);
      const clientData = res.data.data;
      setSelectedClient(clientData);

      const docRes = await api.get(`/clients/${clientId}/documents`);
      setDocuments(docRes.data.data || []);

      await loadInteractions(clientId);

      const validRefs = (clientData.references || []).filter(r => r.name);
      if (validRefs.length < 2) {
        recoverReferences(clientId);
      } else {
        recoveredClientIdRef.current = clientId;
      }
    } catch (err) {
      alert('Could not fetch client profile: ' + (err.response?.data?.message || err.message));
      setReviewModal(false);
    } finally {
      setReviewLoading(false);
    }
  };

  const openActionModal = (client, type) => {
    setSelectedClient(client);
    setActionType(type);
    setActionNotes('');
    setRecommendedProduct('');
    setRecommendedLimit('');
    setActionModal(true);
  };

  // ═══════════════════════════════════════════════════════════
  // NEW: Open Return-to-officer modal
  // ═══════════════════════════════════════════════════════════
  const openReturnModal = (client) => {
    setSelectedClient(client);
    setReturnReason('');
    setReturnAreas({
      personal: false,
      home: false,
      business: false,
      collateral: false,
      documents: false,
    });
    setReturnModal(true);
  };

  const handleAction = async (e) => {
    e.preventDefault();
    if ((actionType === 'rejected' || actionType === 'deactivated') && actionNotes.length < 5) {
      return alert('Please provide a detailed reason (min 5 characters).');
    }

    setActionLoading(true);
    try {
      const payload = { decision: actionType, reason: actionNotes };

      let finalLimit = 0;
      let finalProductId = '';

      if (actionType === 'approved' && recommendedProduct) {
        const prod = products.find(p => p._id === recommendedProduct);
        finalLimit = recommendedLimit ? parseInt(recommendedLimit) : (prod?.maxAmount || 0);
        finalProductId = recommendedProduct;
        payload.reason = `RECOMMENDED LOAN: ${prod?.name || 'N/A'} (Limit: KES ${finalLimit.toLocaleString()}). ${actionNotes}`;
      } else if (actionType === 'approved') {
        payload.reason = actionNotes || 'Approved by manager.';
      }

      await api.patch(`/clients/${selectedClient._id}/approve-reject`, payload);

      if (actionType === 'approved' && finalLimit > 0) {
        try {
          await api.put(`/clients/${selectedClient._id}`, {
            recommendedLoanLimit: finalLimit,
            recommendedLoanProduct: finalProductId,
          });
        } catch (saveErr) {
          console.warn('Note: Could not save recommended limit to client:', saveErr.message);
        }
      }

      setClients(prev => prev.filter(c => c._id !== selectedClient._id));
      setActionModal(false);
      alert(`Client successfully ${actionType === 'approved' ? 'Approved' : 'Deactivated'}.`);
    } catch (err) {
      alert(err.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // NEW: Handle Return-to-officer action
  // ═══════════════════════════════════════════════════════════
  const handleReturnToOfficer = async (e) => {
    e.preventDefault();

    if (returnReason.trim().length < 10) {
      return alert('Please provide a detailed reason (min 10 characters) explaining what the officer needs to fix.');
    }

    const selectedAreas = Object.keys(returnAreas).filter(k => returnAreas[k]);
    if (selectedAreas.length === 0) {
      return alert('Please select at least one area that needs modification.');
    }

    setReturnLoading(true);
    try {
      const areaLabels = {
        personal: 'Personal Information',
        home: 'Home Address',
        business: 'Business Details',
        collateral: 'Collateral',
        documents: 'KYC Documents',
      };

      const areasList = selectedAreas.map(a => areaLabels[a]).join(', ');
      const fullReason = `RETURNED FOR MODIFICATION\n\nAreas to fix: ${areasList}\n\nManager's instructions: ${returnReason.trim()}`;

      // Send decision as 'returned' to backend
      await api.patch(`/clients/${selectedClient._id}/approve-reject`, {
        decision: 'returned',
        reason: fullReason,
        returnAreas: selectedAreas,
      });

      // Also persist return metadata to client record
      try {
        await api.put(`/clients/${selectedClient._id}`, {
          status: 'returned-for-modification',
          returnReason: returnReason.trim(),
          returnAreas: selectedAreas,
          returnedBy: 'manager',
          returnedAt: new Date().toISOString(),
        });
      } catch (saveErr) {
        console.warn('Could not save return metadata:', saveErr.message);
      }

      // Log as interaction
      try {
        await api.post('/clients/interactions', {
          client: selectedClient._id,
          step: 0,
          stepTitle: 'Returned to Officer',
          type: 'note',
          content: `Client profile returned to officer for modification.\n\nAreas: ${areasList}\nInstructions: ${returnReason.trim()}`,
        });
      } catch (e) {
        console.warn('Could not log interaction:', e.message);
      }

      setClients(prev => prev.filter(c => c._id !== selectedClient._id));
      setReturnModal(false);
      alert(`✅ Client profile returned to officer for modification. The officer has been notified.`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to return client to officer');
    } finally {
      setReturnLoading(false);
    }
  };

  const handleVerifyDoc = async (docId) => {
    setVerifyingDocId(docId);
    try {
      const res = await api.patch(`/clients/documents/${docId}/verify`, {
        status: 'verified',
      });
      setDocuments(prev => prev.map(d => {
        if (d._id === docId) {
          return {
            ...d,
            status: 'verified',
            verifiedBy: res.data.data.verifiedBy,
            verifiedAt: res.data.data.verifiedAt,
          };
        }
        return d;
      }));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to verify document');
    } finally {
      setVerifyingDocId(null);
    }
  };

  const handleRejectDoc = async (docId) => {
    if (!rejectionReason.trim() || rejectionReason.trim().length < 3) {
      alert('Please provide a rejection reason (min 3 characters).');
      return;
    }
    setRejectingDocId(docId);
    try {
      await api.patch(`/clients/documents/${docId}/verify`, {
        status: 'rejected',
        reason: rejectionReason.trim(),
      });
      setDocuments(prev => prev.map(d => {
        if (d._id === docId) {
          return {
            ...d,
            status: 'rejected',
            rejectionReason: rejectionReason.trim(),
          };
        }
        return d;
      }));
      setRejectionReason('');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reject document');
    } finally {
      setRejectingDocId(null);
    }
  };

  const handleViewDoc = async (docId, fileName) => {
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const res = await api.get(`/clients/documents/file/${docId}`, {
        responseType: 'blob',
      });

      const contentType = res.headers['content-type'] || 'application/octet-stream';
      const lowerName = (fileName || '').toLowerCase();
      const fallbackType = lowerName.endsWith('.pdf') ? 'application/pdf'
        : lowerName.endsWith('.png') ? 'image/png'
        : lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg') ? 'image/jpeg'
        : 'application/octet-stream';

      const finalType = contentType === 'application/octet-stream' ? fallbackType : contentType;

      const blob = new Blob([res.data], { type: finalType });
      const url = window.URL.createObjectURL(blob) + `#filename=${encodeURIComponent(fileName || 'document')}`;

      const isImage = finalType.startsWith('image/');
      const isPdf = finalType === 'application/pdf' || lowerName.endsWith('.pdf');

      if (isImage || isPdf) {
        const newWindow = window.open(url, '_blank');
        if (!newWindow) {
          setPreviewDoc({ url, fileName, isImage, isPdf });
        }
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Document view failed:', err);
      setPreviewError('Failed to load document. You may not have permission.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadDoc = async (docId, fileName) => {
    try {
      const res = await api.get(`/clients/documents/file/${docId}`, {
        responseType: 'blob',
      });

      const contentType = res.headers['content-type'] || 'application/octet-stream';
      const lowerName = (fileName || '').toLowerCase();
      const fallbackType = lowerName.endsWith('.pdf') ? 'application/pdf'
        : lowerName.endsWith('.png') ? 'image/png'
        : lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg') ? 'image/jpeg'
        : 'application/octet-stream';

      const finalType = contentType === 'application/octet-stream' ? fallbackType : contentType;

      const blob = new Blob([res.data], { type: finalType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Document download failed:', err);
      alert('Failed to download document. You may not have permission.');
    }
  };

  const docSummary = {
    total: documents.length,
    verified: documents.filter(d => d.status === 'verified').length,
    pending: documents.filter(d => d.status === 'pending').length,
    rejected: documents.filter(d => d.status === 'rejected').length,
    allVerified: documents.length > 0 && documents.every(d => d.status === 'verified'),
    hasRejected: documents.some(d => d.status === 'rejected'),
  };

  const pendingApprovalCount = clients.filter(c => c.status === 'pending-approval').length;
  const pendingManagerCount = clients.filter(c => c.status === 'pending-manager-review').length;

  if (loading) return <div className="spinner"></div>;

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending-approval': return 'Pending Approval';
      case 'pending-manager-review': return 'Pending Manager Review';
      default: return status;
    }
  };

  const getStatusBadgeMap = (status) => {
    switch (status) {
      case 'pending-approval': return { 'pending-approval': 'info' };
      case 'pending-manager-review': return { 'pending-manager-review': 'warning' };
      default: return { [status]: 'secondary' };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending-approval': return '#3b82f6';
      case 'pending-manager-review': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', transition: 'all 0.2s ease',
    outline: 'none', background: '#fff'
  };

  const menuItems = [
    { id: 'personal', label: 'Personal Info', icon: '👤' },
    { id: 'home', label: 'Home Details', icon: '🏠' },
    { id: 'business', label: 'Business Details', icon: '🏢' },
    { id: 'collateral', label: 'Collateral', icon: '🔒' },
    {
      id: 'documents',
      label: 'KYC Documents',
      icon: '📄',
      badge: docSummary.pending > 0 ? docSummary.pending : null,
      badgeColor: docSummary.pending > 0 ? '#f59e0b' : '#10b981',
    },
    { id: 'interactions', label: 'Interactions', icon: '📋', badge: interactions.length },
  ];

  const sidebarItemStyle = (id) => ({
    padding: '12px 16px',
    fontSize: '13px',
    cursor: 'pointer',
    background: activeSection === id ? '#eff6ff' : 'transparent',
    borderLeft: activeSection === id ? '3px solid var(--primary, #3b82f6)' : '3px solid transparent',
    color: activeSection === id ? 'var(--primary, #3b82f6)' : '#334155',
    fontWeight: activeSection === id ? '600' : '400',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'all 0.2s',
  });

  const InfoRow = ({ label, value, isNationalId }) => (
    <div style={{ display: 'flex', marginBottom: 8 }}>
      <span className="text-xs text-muted" style={{ width: 140, flexShrink: 0 }}>{label}:</span>
      <span className="text-sm" style={{ fontWeight: 500, flex: 1 }}>
        {isNationalId ? '✅ Provided' : (value || 'N/A')}
      </span>
    </div>
  );

  const PhotoGrid = ({ photos, sectionLabel }) => {
    if (!photos || !Array.isArray(photos) || photos.length === 0) return <p className="text-xs text-muted">No photos uploaded.</p>;
    return (
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
        {photos.map((p, i) => (
          <div
            key={i}
            style={{
              textAlign: 'center',
              cursor: 'pointer',
              borderRadius: 10,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              position: 'relative',
              overflow: 'hidden',
            }}
            onClick={() => setLightbox({
              src: p.image,
              caption: p.description || `${sectionLabel || 'Photo'} ${i + 1}`,
              alt: p.description || `Photo ${i + 1}`,
            })}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.18)';
              e.currentTarget.style.zIndex = '2';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.zIndex = '1';
            }}
            title="Click to enlarge"
          >
            <img
              src={p.image}
              alt={p.description || `Photo ${i + 1}`}
              style={{
                width: 90,
                height: 90,
                objectFit: 'cover',
                borderRadius: 8,
                border: '2px solid #e2e8f0',
                display: 'block',
                transition: 'border-color 0.2s',
              }}
              onMouseEnter={(e) => { e.target.style.borderColor = '#6366f1'; }}
              onMouseLeave={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
            />
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
              borderRadius: '0 0 8px 8px',
              padding: '12px 4px 4px',
              opacity: 0,
              transition: 'opacity 0.2s',
              pointerEvents: 'none',
            }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; }}
            >
              <span style={{ color: '#fff', fontSize: 14 }}>🔍</span>
            </div>
            {p.description && <p className="text-xs text-muted" style={{ width: 90, marginTop: 4 }}>{p.description}</p>}
          </div>
        ))}
      </div>
    );
  };

  const selectedReferences = selectedClient && Array.isArray(selectedClient.references)
    ? selectedClient.references.filter(r => r.name)
    : [];
  const selectedNextOfKin = selectedClient?.nextOfKin;

  const filterTabStyle = (filterId) => ({
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: activeFilter === filterId ? '600' : '400',
    cursor: 'pointer',
    border: 'none',
    borderRadius: '8px',
    background: activeFilter === filterId ? 'var(--primary, #3b82f6)' : '#f1f5f9',
    color: activeFilter === filterId ? '#fff' : '#475569',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-3" style={{ flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Approval Queue</h3>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          {pendingApprovalCount > 0 && (
            <span className="badge badge-info" style={{ fontSize: 12 }}>
              {pendingApprovalCount} Pending Approval
            </span>
          )}
          {pendingManagerCount > 0 && (
            <span className="badge badge-warning" style={{ fontSize: 12 }}>
              {pendingManagerCount} Pending Manager Review
            </span>
          )}
          {clients.length === 0 && (
            <span className="badge badge-secondary">0 Pending</span>
          )}
        </div>
      </div>

      {clients.length === 0 ? (
        <div className="card text-center p-4">
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <p className="text-muted" style={{ fontSize: 15, margin: 0 }}>No clients currently pending approval.</p>
          <p className="text-muted" style={{ fontSize: 13, margin: '8px 0 0' }}>New client submissions will appear here.</p>
        </div>
      ) : (
        <div className="card">
          <div style={{
            padding: '16px 16px 12px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12
          }}>
            <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
              <button
                style={filterTabStyle('all')}
                onClick={() => setActiveFilter('all')}
                onMouseEnter={(e) => { if(activeFilter !== 'all') e.currentTarget.style.background = '#e2e8f0'; }}
                onMouseLeave={(e) => { if(activeFilter !== 'all') e.currentTarget.style.background = '#f1f5f9'; }}
              >
                📋 All ({clients.length})
              </button>
              <button
                style={filterTabStyle('pending-approval')}
                onClick={() => setActiveFilter('pending-approval')}
                onMouseEnter={(e) => { if(activeFilter !== 'pending-approval') e.currentTarget.style.background = '#e2e8f0'; }}
                onMouseLeave={(e) => { if(activeFilter !== 'pending-approval') e.currentTarget.style.background = '#f1f5f9'; }}
              >
                ⏳ Pending Approval ({pendingApprovalCount})
              </button>
              <button
                style={filterTabStyle('pending-manager-review')}
                onClick={() => setActiveFilter('pending-manager-review')}
                onMouseEnter={(e) => { if(activeFilter !== 'pending-manager-review') e.currentTarget.style.background = '#e2e8f0'; }}
                onMouseLeave={(e) => { if(activeFilter !== 'pending-manager-review') e.currentTarget.style.background = '#f1f5f9'; }}
              >
                👔 Manager Review ({pendingManagerCount})
              </button>
            </div>

            <div className="flex gap-2" style={{ alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search by name, ID, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    ...inputStyle,
                    width: '260px',
                    padding: '8px 12px 8px 36px',
                    fontSize: '13px',
                  }}
                />
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 }}>
                  🔍
                </span>
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                  cursor: 'pointer',
                  background: '#fff',
                }}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>National ID</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Date Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                      No clients found matching your filter criteria.
                    </td>
                  </tr>
                ) : (
                  filteredClients.map(c => (
                    <tr key={c._id}>
                      <td className="font-bold" style={{ fontFamily: 'monospace' }}>{c.clientNumber}</td>
                      <td>{c.firstName} {c.lastName}</td>
                      <td>{c.phone}</td>
                      <td>
                        <StatusBadge
                          status={c.status}
                          map={getStatusBadgeMap(c.status)}
                        />
                      </td>
                      <td className="text-xs">{new Date(c.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="flex gap-1" style={{ flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => openReviewModal(c._id)}
                            title="Review client details"
                          >
                            🔍 Review
                          </button>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => openActionModal(c, 'approved')}
                            title="Approve this client"
                          >
                            ✅ Approve
                          </button>
                          {/* NEW: Return to Officer button */}
                          <button
                            className="btn btn-sm"
                            onClick={() => openReturnModal(c)}
                            title="Return this client to the onboarding officer for modifications"
                            style={{
                              background: '#fff7ed',
                              color: '#c2410c',
                              border: '1px solid #fed7aa',
                              fontWeight: 600,
                            }}
                          >
                            ↩️ Return
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => openActionModal(c, 'deactivated')}
                            title="Deactivate this client"
                          >
                            🚫 Deactivate
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filteredClients.length > 0 && (
            <div style={{
              padding: '10px 16px',
              borderTop: '1px solid #e2e8f0',
              fontSize: '12px',
              color: '#64748b',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span>
                Showing {filteredClients.length} of {clients.length} client(s)
                {activeFilter !== 'all' && (
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--primary, #3b82f6)',
                      cursor: 'pointer',
                      marginLeft: 8,
                      fontSize: '12px',
                      textDecoration: 'underline',
                    }}
                    onClick={() => setActiveFilter('all')}
                  >
                    Clear filter
                  </button>
                )}
              </span>
              <span>
                {pendingApprovalCount} pending approval • {pendingManagerCount} pending manager review
              </span>
            </div>
          )}
        </div>
      )}

      {/* LIGHTBOX MODAL */}
      {lightbox && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: lightboxZoomed ? 'zoom-out' : 'zoom-in',
          }}
          onClick={() => setLightboxZoomed(!lightboxZoomed)}
        >
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            style={{
              maxWidth: lightboxZoomed ? 'none' : '90vw',
              maxHeight: lightboxZoomed ? 'none' : '90vh',
              objectFit: 'contain',
              transform: lightboxZoomed ? 'scale(2)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              borderRadius: 4,
            }}
          />
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); setLightboxZoomed(false); }}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: '#fff',
              width: 40,
              height: 40,
              borderRadius: '50%',
              fontSize: 20,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>
          {lightbox.caption && (
            <div style={{
              position: 'absolute',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.7)',
              color: '#fff',
              padding: '8px 20px',
              borderRadius: 8,
              fontSize: 14,
              maxWidth: '80%',
              textAlign: 'center',
            }}>
              {lightbox.caption}
            </div>
          )}
        </div>
      )}

      {/* REVIEW MODAL */}
      <Modal isOpen={reviewModal} onClose={() => { setReviewModal(false); setPreviewDoc(null); setLightbox(null); }} title={null} size="1100px">
        {selectedClient && (
          <div style={{ display: 'flex', gap: 0, minHeight: '600px' }}>

            {/* LEFT SIDEBAR */}
            <div style={{
              width: '240px', flexShrink: 0, background: '#f8fafc',
              borderRight: '1px solid #e2e8f0',
              display: 'flex', flexDirection: 'column'
            }}>
              <div style={{ padding: '20px 16px', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 4px', fontSize: '15px' }}>{selectedClient.firstName} {selectedClient.lastName}</h4>
                <p style={{ margin: '0 0 2px', fontSize: '13px', fontFamily: 'monospace', fontWeight: 600, color: '#1e40af' }}>
                  {selectedClient.clientNumber}
                </p>
                <p className="text-xs text-muted" style={{ margin: 0 }}>{selectedClient.phone}</p>
                <div style={{ marginTop: 8 }}>
                  <StatusBadge
                    status={selectedClient.status}
                    map={getStatusBadgeMap(selectedClient.status)}
                  />
                </div>
                <div style={{
                  marginTop: 8,
                  padding: '6px 10px',
                  borderRadius: 6,
                  background: selectedClient.status === 'pending-manager-review' ? '#fef9c3' : '#eff6ff',
                  border: `1px solid ${selectedClient.status === 'pending-manager-review' ? '#fde047' : '#bfdbfe'}`,
                  fontSize: '11px',
                  color: selectedClient.status === 'pending-manager-review' ? '#854d0e' : '#1e40af',
                  fontWeight: 500,
                }}>
                  {selectedClient.status === 'pending-manager-review'
                    ? '👔 Awaiting your review decision'
                    : '⏳ New submission awaiting review'}
                </div>
              </div>

              <div style={{ padding: '8px 0', flex: 1 }}>
                {menuItems.map(item => (
                  <div
                    key={item.id}
                    style={sidebarItemStyle(item.id)}
                    onClick={() => { setActiveSection(item.id); setPreviewDoc(null); setLightbox(null); }}
                    onMouseEnter={(e) => { if(activeSection !== item.id) e.currentTarget.style.background = '#f1f5f9'; }}
                    onMouseLeave={(e) => { if(activeSection !== item.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ fontSize: '16px' }}>{item.icon}</span>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {item.badge > 0 && (
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        background: item.badgeColor || '#6366f1',
                        color: '#fff',
                        padding: '1px 7px', borderRadius: 10, lineHeight: '18px',
                      }}>
                        {item.badge}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button
                  className="btn btn-success w-full"
                  style={{ fontSize: '13px' }}
                  onClick={() => { setReviewModal(false); openActionModal(selectedClient, 'approved'); }}
                >
                  ✅ Approve Client
                </button>
                {/* NEW: Return to Officer button in sidebar */}
                <button
                  className="btn w-full"
                  style={{
                    fontSize: '13px',
                    background: '#fff7ed',
                    color: '#c2410c',
                    border: '1px solid #fdba74',
                    fontWeight: 600,
                  }}
                  onClick={() => { setReviewModal(false); openReturnModal(selectedClient); }}
                >
                  ↩️ Return to Officer
                </button>
                <button
                  className="btn btn-danger w-full"
                  style={{ fontSize: '13px', background: '#fff', color: '#dc2626', border: '1px solid #dc2626' }}
                  onClick={() => { setReviewModal(false); openActionModal(selectedClient, 'deactivated'); }}
                >
                  🚫 Deactivate
                </button>
              </div>
            </div>

            {/* RIGHT CONTENT AREA */}
            <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', maxHeight: '80vh', background: '#fff' }}>

              {reviewLoading ? (
                <div className="spinner" style={{ marginTop: 40 }}></div>
              ) : (
                <>
                  {/* PERSONAL INFO */}
                  {activeSection === 'personal' && (
                    <div>
                      <div className="flex justify-between items-center mb-2" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
                        <h4 style={{ margin: 0, fontSize: '16px' }}>Personal Information</h4>
                        <span className="badge badge-info">{selectedClient.riskRating?.toUpperCase() || 'MEDIUM'} Risk</span>
                      </div>

                      <div className="card p-2 mb-2" style={{ background: '#f8fafc' }}>
                        <h6 className="text-xs text-muted mb-1" style={{ textTransform: 'uppercase' }}>Basic Details</h6>
                        <InfoRow label="National ID" value={selectedClient.clientNumber} />
                        <InfoRow label="Date of Birth" value={selectedClient.dateOfBirth ? new Date(selectedClient.dateOfBirth).toLocaleDateString() : ''} />
                        <InfoRow label="Gender" value={selectedClient.gender} />
                        <InfoRow label="Marital Status" value={selectedClient.maritalStatus} />
                      </div>

                      <div className="card p-2 mb-2" style={{ background: '#f8fafc' }}>
                        <h6 className="text-xs text-muted mb-1" style={{ textTransform: 'uppercase' }}>Employment & Income</h6>
                        <InfoRow label="Occupation" value={selectedClient.occupation} />
                        <InfoRow label="Employer" value={selectedClient.employer} />
                        <InfoRow label="Monthly Income" value={selectedClient.monthlyIncome ? `KES ${selectedClient.monthlyIncome.toLocaleString()}` : ''} />
                      </div>

                      <div className="card p-2 mb-2" style={{ background: '#fefce8', borderLeft: '3px solid #eab308' }}>
                        <h6 className="text-xs mb-1" style={{ textTransform: 'uppercase', color: '#854d0e', fontWeight: 600 }}>👤 Next of Kin</h6>
                        {selectedNextOfKin && selectedNextOfKin.name ? (
                          <>
                            <InfoRow label="Name" value={selectedNextOfKin.name} />
                            <InfoRow label="Relationship" value={selectedNextOfKin.relationship} />
                            <InfoRow label="Phone" value={selectedNextOfKin.phone} />
                            <InfoRow label="ID Number" value={selectedNextOfKin.nationalId} />
                          </>
                        ) : (
                          <p className="text-xs text-muted" style={{ margin: 0, fontStyle: 'italic' }}>No next of kin recorded during onboarding</p>
                        )}
                      </div>

                      <div className="card p-2 mb-2" style={{ background: '#f0fdf4', borderLeft: '3px solid #22c55e' }}>
                        <div className="flex justify-between items-center mb-1">
                          <h6 className="text-xs mb-0" style={{ textTransform: 'uppercase', color: '#166534', fontWeight: 600 }}>
                            📋 Referees / References ({selectedReferences.length})
                          </h6>
                          {recoveringRefs && (
                            <span className="text-xs text-muted">
                              <i className="fa fa-spinner fa-spin"></i> Recovering...
                            </span>
                          )}
                        </div>
                        {selectedReferences.length === 0 ? (
                          <div>
                            <p className="text-xs text-muted" style={{ margin: '0 0 6px', fontStyle: 'italic' }}>No referees recorded during onboarding</p>
                            {!recoveringRefs && selectedClient && (
                              <button
                                type="button"
                                className="btn btn-outline btn-xs"
                                onClick={() => recoverReferences(selectedClient._id)}
                                disabled={recoveringRefs}
                                style={{ fontSize: '11px', padding: '3px 10px' }}
                              >
                                <i className="fa fa-refresh"></i> Recover from previous loans
                              </button>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {selectedReferences.map((ref, index) => (
                              <div
                                key={index}
                                style={{
                                  background: '#fff',
                                  border: '1px solid #bbf7d0',
                                  borderRadius: 8,
                                  padding: '8px 12px',
                                }}
                              >
                                <div className="flex items-center gap-1 mb-1">
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    background: '#22c55e',
                                    color: '#fff',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    flexShrink: 0,
                                  }}>
                                    {index + 1}
                                  </span>
                                  <strong style={{ fontSize: '13px' }}>{ref.name}</strong>
                                  {ref.relationship && (
                                    <span style={{
                                      fontSize: '10px',
                                      fontWeight: 600,
                                      padding: '1px 8px',
                                      borderRadius: 10,
                                      background: '#dcfce7',
                                      color: '#166534',
                                    }}>
                                      {ref.relationship}
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: 20, marginLeft: 28 }}>
                                  <div>
                                    <span className="text-xs text-muted">Phone: </span>
                                    <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>{ref.phone || 'N/A'}</span>
                                  </div>
                                  <div>
                                    <span className="text-xs text-muted">ID: </span>
                                    <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>{ref.idNumber || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {selectedClient.recommendedLoanLimit > 0 && (
                        <div className="card p-2 mb-2" style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
                          <h6 className="text-xs mb-1" style={{ textTransform: 'uppercase', color: '#166534', fontWeight: 600 }}>💳 Manager-Recommended Loan Limit</h6>
                          <InfoRow label="Recommended Limit" value={`KES ${selectedClient.recommendedLoanLimit.toLocaleString()}`} />
                          {selectedClient.recommendedLoanProduct && (() => {
                            const rp = products.find(p => p._id === selectedClient.recommendedLoanProduct);
                            return rp ? <InfoRow label="Recommended Product" value={rp.name} /> : null;
                          })()}
                        </div>
                      )}

                      <div className="card p-2" style={{ background: '#f8fafc' }}>
                        <h6 className="text-xs text-muted mb-1" style={{ textTransform: 'uppercase' }}>Assignment</h6>
                        <InfoRow label="Assigned To" value={selectedClient.assignedTo ? `${selectedClient.assignedTo.firstName} ${selectedClient.assignedTo.lastName}` : 'Unassigned'} />
                        <InfoRow label="Branch" value={selectedClient.branch?.name || 'Unassigned'} />
                        <InfoRow label="Notes" value={selectedClient.notes} />
                      </div>
                    </div>
                  )}

                  {/* HOME DETAILS */}
                  {activeSection === 'home' && (
                    <div>
                      <h4 style={{ margin: '0 0 16px', fontSize: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>Home Details</h4>
                      <div className="card p-2 mb-2" style={{ background: '#f8fafc' }}>
                        <h6 className="text-xs text-muted mb-1" style={{ textTransform: 'uppercase' }}>📍 Address & Location</h6>
                        <InfoRow label="Street / Estate" value={selectedClient.address?.street} />
                        <InfoRow label="City / Town" value={selectedClient.address?.city} />
                        <InfoRow label="County" value={selectedClient.address?.state} />
                        <InfoRow label="Description" value={selectedClient.homeDescription} />
                        <InfoRow
                          label="GPS Coordinates"
                          value={selectedClient.gpsLocation
                            ? `${selectedClient.gpsLocation.latitude.toFixed(6)}, ${selectedClient.gpsLocation.longitude.toFixed(6)}`
                            : '❌ Not Captured' }
                        />
                      </div>
                      <div className="card p-2" style={{ background: '#f8fafc' }}>
                        <h6 className="text-xs text-muted mb-1" style={{ textTransform: 'uppercase' }}>
                          📸 Home Photos
                          <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af', marginLeft: 8, textTransform: 'none' }}>(click to enlarge)</span>
                        </h6>
                        <PhotoGrid photos={selectedClient.homePhotos} sectionLabel="Home Photo" />
                      </div>
                    </div>
                  )}

                  {/* BUSINESS DETAILS */}
                  {activeSection === 'business' && (
                    <div>
                      <h4 style={{ margin: '0 0 16px', fontSize: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>Business Details</h4>
                      <div className="card p-2 mb-2" style={{ background: '#f8fafc' }}>
                        <h6 className="text-xs text-muted mb-1" style={{ textTransform: 'uppercase' }}>🏢 Business Info</h6>
                        <InfoRow label="Business Name" value={selectedClient.businessName} />
                        <InfoRow label="Business Type" value={selectedClient.businessType} />
                        <InfoRow label="Sector" value={selectedClient.businessSector} />
                        <InfoRow label="Address" value={selectedClient.businessAddress} />
                        <InfoRow label="Description" value={selectedClient.businessDescription} />
                      </div>
                      <div className="card p-2 mb-2" style={{ background: '#f8fafc' }}>
                        <h6 className="text-xs text-muted mb-1" style={{ textTransform: 'uppercase' }}>📍 Business Location</h6>
                        <InfoRow
                          label="GPS Coordinates"
                          value={selectedClient.businessGpsLocation
                            ? `${selectedClient.businessGpsLocation.latitude.toFixed(6)}, ${selectedClient.businessGpsLocation.longitude.toFixed(6)}`
                            : '❌ Not Captured' }
                        />
                      </div>
                      <div className="card p-2" style={{ background: '#f8fafc' }}>
                        <h6 className="text-xs text-muted mb-1" style={{ textTransform: 'uppercase' }}>
                          📸 Business Photos
                          <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af', marginLeft: 8, textTransform: 'none' }}>(click to enlarge)</span>
                        </h6>
                        <PhotoGrid photos={selectedClient.businessPhotos} sectionLabel="Business Photo" />
                      </div>
                    </div>
                  )}

                  {/* COLLATERAL */}
                  {activeSection === 'collateral' && (
                    <div>
                      <h4 style={{ margin: '0 0 16px', fontSize: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>Collateral</h4>
                      {selectedClient.collateral && Array.isArray(selectedClient.collateral) && selectedClient.collateral.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          {selectedClient.collateral.map((c, i) => (
                            <div key={i} className="card p-2" style={{ background: '#f8fafc', display: 'flex', gap: 16 }}>
                              {c.image ? (
                                <div
                                  style={{ position: 'relative', cursor: 'pointer', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}
                                  onClick={() => setLightbox({
                                    src: c.image,
                                    caption: `Collateral Item ${i + 1}: ${c.type}${c.description ? ` — ${c.description}` : ''}`,
                                    alt: `Collateral ${i + 1}`,
                                  })}
                                  title="Click to enlarge"
                                >
                                  <img
                                    src={c.image}
                                    alt={`Collateral ${i + 1}`}
                                    style={{
                                      width: 120, height: 120, objectFit: 'cover',
                                      borderRadius: 8, border: '2px solid #e2e8f0',
                                      display: 'block', transition: 'transform 0.2s, border-color 0.2s',
                                    }}
                                    onMouseEnter={(e) => { e.target.style.transform = 'scale(1.05)'; e.target.style.borderColor = '#6366f1'; }}
                                    onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; e.target.style.borderColor = '#e2e8f0'; }}
                                  />
                                  <div style={{
                                    position: 'absolute', inset: 0,
                                    background: 'rgba(0,0,0,0.35)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    opacity: 0, transition: 'opacity 0.2s', borderRadius: 8, pointerEvents: 'none',
                                  }}
                                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; }}
                                  >
                                    <span style={{ fontSize: 28, color: '#fff' }}>🔍</span>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ width: 120, height: 120, background: '#e2e8f0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#94a3b8', fontSize: '12px' }}>No Image</div>
                              )}
                              <div style={{ flex: 1 }}>
                                <p className="text-xs text-muted" style={{ textTransform: 'uppercase', marginBottom: 4 }}>Item {i + 1}</p>
                                <InfoRow label="Type" value={c.type} />
                                <InfoRow label="Description" value={c.description} />
                                <InfoRow label="Estimated Value" value={`KES ${c.estimatedValue?.toLocaleString() || '0'}`} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="card p-3 text-center" style={{ background: '#f8fafc' }}>
                          <p className="text-muted">No collateral registered during onboarding.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* KYC DOCUMENTS */}
                  {activeSection === 'documents' && (
                    <div>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        borderBottom: '1px solid #e2e8f0', paddingBottom: 8, marginBottom: 16
                      }}>
                        <h4 style={{ margin: 0, fontSize: '16px' }}>KYC Documents</h4>
                        {documents.length > 0 && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <span style={{
                              fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
                              background: docSummary.verified > 0 ? '#dcfce7' : '#f3f4f6',
                              color: docSummary.verified > 0 ? '#166534' : '#9ca3af',
                              border: `1px solid ${docSummary.verified > 0 ? '#86efac' : '#e5e7eb'}`,
                            }}>✅ {docSummary.verified} Verified</span>
                            <span style={{
                              fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
                              background: docSummary.pending > 0 ? '#fef9c3' : '#f3f4f6',
                              color: docSummary.pending > 0 ? '#854d0e' : '#9ca3af',
                              border: `1px solid ${docSummary.pending > 0 ? '#fde047' : '#e5e7eb'}`,
                            }}>⏳ {docSummary.pending} Pending</span>
                            {docSummary.rejected > 0 && (
                              <span style={{
                                fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
                                background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
                              }}>❌ {docSummary.rejected} Rejected</span>
                            )}
                          </div>
                        )}
                      </div>

                      {docSummary.hasRejected && (
                        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: '#fff7ed', border: '1px solid #fed7aa', fontSize: 13, color: '#9a3412' }}>
                          ⚠️ <strong>{docSummary.rejected} document(s) rejected.</strong> You can return this client to the LO using the <strong>"Return to Officer"</strong> button to fix the issues.
                        </div>
                      )}

                      {docSummary.allVerified && documents.length > 0 && (
                        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: '#f0fdf4', border: '1px solid #86efac', fontSize: 13, color: '#166534' }}>
                          ✅ <strong>All {docSummary.total} document(s) verified.</strong> This client is ready for approval.
                        </div>
                      )}

                      {documents.length > 0 ? (
                        <div className="table-container" style={{ border: '1px solid #e2e8f0', borderRadius: 8 }}>
                          <table style={{ width: '100%' }}>
                            <thead>
                              <tr style={{ background: '#f8fafc' }}>
                                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: 600 }}>Category</th>
                                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: 600 }}>Document</th>
                                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: 600 }}>Status</th>
                                <th style={{ textAlign: 'center', padding: '10px 12px', fontSize: '12px', fontWeight: 600 }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {documents.map(doc => (
                                <tr key={doc._id}>
                                  <td style={{ padding: '10px 12px', fontSize: '13px' }}>
                                    <span style={{
                                      textTransform: 'uppercase',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      color: '#64748b',
                                      background: '#f1f5f9',
                                      padding: '3px 8px',
                                      borderRadius: 4,
                                    }}>
                                      {doc.documentType || 'N/A'}
                                    </span>
                                  </td>
                                  <td style={{ padding: '10px 12px', fontSize: '13px' }}>
                                    <div style={{ fontWeight: 500 }}>{doc.fileName || 'Unnamed'}</div>
                                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                      {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : ''}
                                    </div>
                                  </td>
                                  <td style={{ padding: '10px 12px' }}>
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      padding: '3px 10px',
                                      borderRadius: 12,
                                      background: doc.status === 'verified' ? '#dcfce7'
                                        : doc.status === 'rejected' ? '#fef2f2'
                                        : '#fef9c3',
                                      color: doc.status === 'verified' ? '#166534'
                                        : doc.status === 'rejected' ? '#991b1b'
                                        : '#854d0e',
                                    }}>
                                      {doc.status === 'verified' ? '✅ Verified'
                                        : doc.status === 'rejected' ? '❌ Rejected'
                                        : '⏳ Pending'}
                                    </span>
                                    {doc.status === 'rejected' && doc.rejectionReason && (
                                      <div style={{ fontSize: '11px', color: '#991b1b', marginTop: 4, fontStyle: 'italic' }}>
                                        Reason: {doc.rejectionReason}
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                    <div className="flex gap-1" style={{ justifyContent: 'center' }}>
                                      <button
                                        className="btn btn-xs btn-outline"
                                        onClick={() => handleViewDoc(doc._id, doc.fileName)}
                                        disabled={previewLoading}
                                        title="View document"
                                        style={{ fontSize: '11px', padding: '4px 8px' }}
                                      >
                                        👁️
                                      </button>
                                      <button
                                        className="btn btn-xs btn-outline"
                                        onClick={() => handleDownloadDoc(doc._id, doc.fileName)}
                                        title="Download document"
                                        style={{ fontSize: '11px', padding: '4px 8px' }}
                                      >
                                        📥
                                      </button>
                                      {doc.status !== 'verified' && (
                                        <button
                                          className="btn btn-xs btn-success"
                                          onClick={() => handleVerifyDoc(doc._id)}
                                          disabled={verifyingDocId === doc._id}
                                          title="Verify document"
                                          style={{ fontSize: '11px', padding: '4px 8px' }}
                                        >
                                          {verifyingDocId === doc._id ? '⏳' : '✅'}
                                        </button>
                                      )}
                                      {doc.status !== 'rejected' && doc.status !== 'verified' && (
                                        <button
                                          className="btn btn-xs btn-danger"
                                          onClick={() => {
                                            if (rejectionReason.trim().length >= 3) {
                                              handleRejectDoc(doc._id);
                                            } else {
                                              setRejectingDocId(doc._id);
                                            }
                                          }}
                                          disabled={rejectingDocId === doc._id}
                                          title="Reject document"
                                          style={{ fontSize: '11px', padding: '4px 8px' }}
                                        >
                                          ❌
                                        </button>
                                      )}
                                    </div>
                                    {rejectingDocId === doc._id && (
                                      <div style={{ marginTop: 8, display: 'flex', gap: 4, alignItems: 'center' }}>
                                        <input
                                          type="text"
                                          placeholder="Rejection reason..."
                                          value={rejectionReason}
                                          onChange={(e) => setRejectionReason(e.target.value)}
                                          style={{
                                            flex: 1,
                                            padding: '4px 8px',
                                            fontSize: '11px',
                                            borderRadius: 4,
                                            border: '1px solid #fecaca'
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' && rejectionReason.trim().length >= 3) {
                                              handleRejectDoc(doc._id);
                                            }
                                            if (e.key === 'Escape') {
                                              setRejectingDocId(null);
                                              setRejectionReason('');
                                            }
                                          }}
                                          autoFocus
                                        />
                                        <button
                                          className="btn btn-xs btn-danger"
                                          onClick={() => handleRejectDoc(doc._id)}
                                          disabled={rejectionReason.trim().length < 3}
                                          style={{ fontSize: '11px', padding: '4px 8px' }}
                                        >
                                          Confirm
                                        </button>
                                        <button
                                          className="btn btn-xs btn-outline"
                                          onClick={() => { setRejectingDocId(null); setRejectionReason(''); }}
                                          style={{ fontSize: '11px', padding: '4px 8px' }}
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="card p-3 text-center" style={{ background: '#f8fafc' }}>
                          <p className="text-muted">No documents uploaded.</p>
                        </div>
                      )}

                      {previewError && (
                        <div style={{
                          marginTop: 12,
                          padding: '10px 14px',
                          borderRadius: 8,
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          fontSize: 13,
                          color: '#991b1b'
                        }}>
                          {previewError}
                        </div>
                      )}
                    </div>
                  )}

                  {/* INTERACTIONS */}
                  {activeSection === 'interactions' && (
                    <div>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        borderBottom: '1px solid #e2e8f0', paddingBottom: 8, marginBottom: 16
                      }}>
                        <h4 style={{ margin: 0, fontSize: '16px' }}>Interactions</h4>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => setInteractionModalOpen(true)}
                          style={{ fontSize: '12px' }}
                        >
                          + Add Note
                        </button>
                      </div>

                      {interactionsLoading ? (
                        <div className="spinner" style={{ marginTop: 20 }}></div>
                      ) : interactions.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          {interactions.map((int, i) => {
                            const typeInfo = INTERACTION_TYPES.find(t => t.value === int.type) || INTERACTION_TYPES[0];
                            return (
                              <div
                                key={int._id || i}
                                style={{
                                  background: '#f8fafc',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: 8,
                                  padding: '12px 16px',
                                  borderLeft: `4px solid ${typeInfo.color}`,
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 14 }}>{typeInfo.label}</span>
                                    <span style={{ fontSize: '11px', fontWeight: 600, color: typeInfo.color, background: `${typeInfo.color}15`, padding: '2px 8px', borderRadius: 4 }}>
                                      {int.stepTitle || 'General'}
                                    </span>
                                  </div>
                                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                    {int.createdAt ? new Date(int.createdAt).toLocaleString() : ''}
                                  </span>
                                </div>
                                <p style={{ margin: 0, fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap' }}>
                                  {int.content}
                                </p>
                                {int.createdBy && (
                                  <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#94a3b8' }}>
                                    By: {int.createdBy.firstName} {int.createdBy.lastName}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="card p-3 text-center" style={{ background: '#f8fafc' }}>
                          <p className="text-muted">No interactions recorded yet.</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* ACTION MODAL (Approve/Deactivate) */}
      <Modal
        isOpen={actionModal}
        onClose={() => setActionModal(false)}
        title={actionType === 'approved' ? '✅ Approve Client' : '🚫 Deactivate Client'}
        size="500px"
      >
        {selectedClient && (
          <form onSubmit={handleAction}>
            <div style={{
              padding: '12px 16px',
              marginBottom: 16,
              background: '#f8fafc',
              borderRadius: 8,
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: actionType === 'approved' ? '#dcfce7' : '#fef2f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                }}>
                  {actionType === 'approved' ? '✅' : '🚫'}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>
                    {selectedClient.firstName} {selectedClient.lastName}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', fontFamily: 'monospace' }}>
                    {selectedClient.clientNumber}
                  </div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <StatusBadge
                    status={selectedClient.status}
                    map={getStatusBadgeMap(selectedClient.status)}
                  />
                </div>
              </div>
            </div>

            {actionType === 'approved' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                  💳 Recommend Loan Product (Optional)
                </label>
                <select
                  value={recommendedProduct}
                  onChange={(e) => setRecommendedProduct(e.target.value)}
                  style={{ ...inputStyle, marginBottom: 10 }}
                >
                  <option value="">-- Select Product --</option>
                  {products.map(p => (
                    <option key={p._id} value={p._id}>
                      {p.name} (Max: KES {p.maxAmount?.toLocaleString()})
                    </option>
                  ))}
                </select>

                {recommendedProduct && (
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                      💰 Recommended Loan Limit
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute',
                        left: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '13px',
                        color: '#64748b',
                        fontWeight: 600,
                      }}>
                        KES
                      </span>
                      <input
                        type="number"
                        value={recommendedLimit}
                        onChange={(e) => setRecommendedLimit(e.target.value)}
                        placeholder="Enter limit amount"
                        style={{ ...inputStyle, paddingLeft: 48 }}
                        min="0"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                {actionType === 'deactivated' ? '⚠️ Reason for Deactivation *' : '📝 Notes (Optional)'}
              </label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder={actionType === 'deactivated'
                  ? 'Please provide a detailed reason for deactivating this client...'
                  : 'Add any notes about this decision...'}
                style={{
                  ...inputStyle,
                  minHeight: actionType === 'deactivated' ? 100 : 70,
                  resize: 'vertical',
                  borderColor: actionType === 'deactivated' && actionNotes.length < 5 ? '#fecaca' : '#d1d5db',
                }}
                required={actionType === 'deactivated'}
              />
              {actionType === 'deactivated' && actionNotes.length > 0 && actionNotes.length < 5 && (
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#dc2626' }}>
                  Minimum 5 characters required
                </p>
              )}
            </div>

            {actionType === 'deactivated' && (
              <div style={{
                padding: '10px 14px',
                marginBottom: 16,
                borderRadius: 8,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                fontSize: '13px',
                color: '#991b1b',
              }}>
                ⚠️ <strong>Warning:</strong> This will deactivate the client. They will need to be re-onboarded to use the system again.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setActionModal(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn ${actionType === 'approved' ? 'btn-success' : 'btn-danger'}`}
                disabled={actionLoading || (actionType === 'deactivated' && actionNotes.length < 5)}
              >
                {actionLoading ? (
                  <><i className="fa fa-spinner fa-spin"></i> Processing...</>
                ) : (
                  actionType === 'approved' ? '✅ Confirm Approval' : '🚫 Confirm Deactivation'
                )}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* ═══════════════════════════════════════════════════════════
          NEW: RETURN TO OFFICER MODAL
          ═══════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={returnModal}
        onClose={() => setReturnModal(false)}
        title="↩️ Return to Officer for Modification"
        size="550px"
      >
        {selectedClient && (
          <form onSubmit={handleReturnToOfficer}>
            {/* Client summary */}
            <div style={{
              padding: '12px 16px',
              marginBottom: 16,
              background: '#fff7ed',
              borderRadius: 8,
              border: '1px solid #fed7aa'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: '#ffedd5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                }}>
                  ↩️
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>
                    {selectedClient.firstName} {selectedClient.lastName}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9a3412', fontFamily: 'monospace' }}>
                    {selectedClient.clientNumber}
                  </div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <StatusBadge
                    status={selectedClient.status}
                    map={getStatusBadgeMap(selectedClient.status)}
                  />
                </div>
              </div>
            </div>

            {/* Info banner */}
            <div style={{
              padding: '12px 14px',
              marginBottom: 16,
              borderRadius: 8,
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              fontSize: '13px',
              color: '#1e40af',
              lineHeight: 1.5,
            }}>
              ℹ️ This will send the client profile back to the onboarding officer with your instructions. The officer will be able to make modifications and resubmit for approval.
            </div>

            {/* Areas to modify */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: 8, color: '#334155' }}>
                📋 Areas that need modification *
              </label>
              <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 10px' }}>
                Select all sections the officer needs to review/fix:
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { key: 'personal', label: '👤 Personal Information' },
                  { key: 'home', label: '🏠 Home Address' },
                  { key: 'business', label: '🏢 Business Details' },
                  { key: 'collateral', label: '🔒 Collateral' },
                  { key: 'documents', label: '📄 KYC Documents' },
                ].map(area => (
                  <label
                    key={area.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: `1.5px solid ${returnAreas[area.key] ? '#f97316' : '#e2e8f0'}`,
                      background: returnAreas[area.key] ? '#fff7ed' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={returnAreas[area.key]}
                      onChange={(e) => setReturnAreas(prev => ({ ...prev, [area.key]: e.target.checked }))}
                      style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#f97316' }}
                    />
                    {area.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Modification instructions */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                📝 Modification Instructions *
              </label>
              <textarea
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="Provide detailed instructions for the officer. Explain exactly what needs to be fixed, upgraded, or modified before resubmission..."
                style={{
                  ...inputStyle,
                  minHeight: 120,
                  resize: 'vertical',
                  borderColor: returnReason.length > 0 && returnReason.length < 10 ? '#fecaca' : '#d1d5db',
                }}
                required
              />
              {returnReason.length > 0 && returnReason.length < 10 && (
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#dc2626' }}>
                  Minimum 10 characters required
                </p>
              )}
              <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#9ca3af' }}>
                Be specific — the officer will see these instructions when they open the profile.
              </p>
            </div>

            {/* Warning */}
            <div style={{
              padding: '10px 14px',
              marginBottom: 16,
              borderRadius: 8,
              background: '#fff7ed',
              border: '1px solid #fed7aa',
              fontSize: '13px',
              color: '#9a3412',
            }}>
              ⚠️ <strong>Note:</strong> The client profile will be moved to the officer's "Returned for Modification" queue. They must make the requested changes and resubmit before you can approve.
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setReturnModal(false)}
                disabled={returnLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn"
                disabled={returnLoading || returnReason.trim().length < 10 || !Object.values(returnAreas).some(v => v)}
                style={{
                  background: returnLoading || returnReason.trim().length < 10 || !Object.values(returnAreas).some(v => v)
                    ? '#fed7aa'
                    : 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 600,
                  cursor: returnLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {returnLoading ? (
                  <><i className="fa fa-spinner fa-spin"></i> Returning...</>
                ) : (
                  '↩️ Return to Officer'
                )}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* ADD INTERACTION MODAL */}
      <Modal
        isOpen={interactionModalOpen}
        onClose={() => { setInteractionModalOpen(false); setNewInteractionType('note'); setNewInteractionContent(''); }}
        title="📝 Add Interaction Note"
        size="450px"
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: 8, color: '#334155' }}>
            Type
          </label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {INTERACTION_TYPES.map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => setNewInteractionType(t.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: `2px solid ${newInteractionType === t.value ? t.color : '#e2e8f0'}`,
                  background: newInteractionType === t.value ? `${t.color}15` : '#fff',
                  color: newInteractionType === t.value ? t.color : '#64748b',
                  fontSize: '12px',
                  fontWeight: newInteractionType === t.value ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: 6, color: '#334155' }}>
            Content *
          </label>
          <textarea
            value={newInteractionContent}
            onChange={(e) => setNewInteractionContent(e.target.value)}
            placeholder="Enter your note here..."
            style={{
              ...inputStyle,
              minHeight: 120,
              resize: 'vertical',
            }}
            autoFocus
          />
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => { setInteractionModalOpen(false); setNewInteractionType('note'); setNewInteractionContent(''); }}
            disabled={savingInteraction}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveInteraction}
            disabled={savingInteraction || !newInteractionContent.trim()}
          >
            {savingInteraction ? (
              <><i className="fa fa-spinner fa-spin"></i> Saving...</>
            ) : (
              '💾 Save Note'
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
}